/**
 * PriceHistoryChart - Interactive price history visualization
 * Uses Recharts for beautiful, responsive charts
 */
import React, { useState, useEffect } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, Area, AreaChart
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Loader2, TrendingUp, TrendingDown, Minus, Calendar } from 'lucide-react';
import { useCountry } from '../context/CountryContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Color palette for multiple lines
const CHART_COLORS = [
  '#3b82f6', // blue
  '#10b981', // green
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // purple
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#84cc16', // lime
];

const CustomTooltip = ({ active, payload, label, currency }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border">
        <p className="text-sm font-medium text-gray-600 mb-2">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            <span className="font-medium">{entry.name}: </span>
            {currency}{entry.value?.toLocaleString()}/mo
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export function SinglePriceHistory({ listingId, title, showCard = true }) {
  const { formatCurrency, currency } = useCountry();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    fetchHistory();
  }, [listingId, period]);
  
  const fetchHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/price-history/${listingId}?days=${period}`);
      if (response.ok) {
        const result = await response.json();
        setData(result);
      } else {
        setError('Failed to load price history');
      }
    } catch (err) {
      console.error('Error fetching price history:', err);
      setError('Failed to load price history');
    } finally {
      setLoading(false);
    }
  };
  
  const getTrendIcon = () => {
    if (!data?.statistics) return <Minus className="h-4 w-4" />;
    const change = data.statistics.total_change_percent;
    if (change > 0) return <TrendingUp className="h-4 w-4 text-red-500" />;
    if (change < 0) return <TrendingDown className="h-4 w-4 text-green-500" />;
    return <Minus className="h-4 w-4 text-gray-400" />;
  };
  
  const getTrendColor = () => {
    if (!data?.statistics) return 'text-gray-500';
    const change = data.statistics.total_change_percent;
    if (change > 0) return 'text-red-500';
    if (change < 0) return 'text-green-500';
    return 'text-gray-500';
  };
  
  const content = (
    <>
      {/* Period selector */}
      <div className="flex items-center gap-2 mb-4">
        <Calendar className="h-4 w-4 text-gray-400" />
        <div className="flex gap-1">
          {[7, 30, 90, 180].map((days) => (
            <Button
              key={days}
              variant={period === days ? "default" : "outline"}
              size="sm"
              onClick={() => setPeriod(days)}
              className="h-7 px-2 text-xs"
            >
              {days}d
            </Button>
          ))}
        </div>
      </div>
      
      {loading ? (
        <div className="h-[200px] flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
        </div>
      ) : error ? (
        <div className="h-[200px] flex items-center justify-center text-gray-500">
          {error}
        </div>
      ) : data ? (
        <>
          {/* Stats row */}
          <div className="grid grid-cols-4 gap-2 mb-4 text-center">
            <div className="p-2 bg-gray-50 rounded">
              <p className="text-xs text-gray-500">Current</p>
              <p className="text-sm font-bold text-blue-600">
                {currency}{data.current_price?.toLocaleString()}
              </p>
            </div>
            <div className="p-2 bg-gray-50 rounded">
              <p className="text-xs text-gray-500">Min</p>
              <p className="text-sm font-medium text-green-600">
                {currency}{data.statistics?.min_price?.toLocaleString()}
              </p>
            </div>
            <div className="p-2 bg-gray-50 rounded">
              <p className="text-xs text-gray-500">Max</p>
              <p className="text-sm font-medium text-red-600">
                {currency}{data.statistics?.max_price?.toLocaleString()}
              </p>
            </div>
            <div className="p-2 bg-gray-50 rounded">
              <p className="text-xs text-gray-500">Change</p>
              <p className={`text-sm font-medium flex items-center justify-center gap-1 ${getTrendColor()}`}>
                {getTrendIcon()}
                {data.statistics?.total_change_percent?.toFixed(1)}%
              </p>
            </div>
          </div>
          
          {/* Chart */}
          <div className="h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.history} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                <defs>
                  <linearGradient id="priceGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 10 }} 
                  tickFormatter={(value) => value ? new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                />
                <YAxis 
                  tick={{ fontSize: 10 }}
                  tickFormatter={(value) => `${currency}${(value/1000).toFixed(0)}k`}
                  width={50}
                />
                <Tooltip content={<CustomTooltip currency={currency} />} />
                <Area 
                  type="monotone" 
                  dataKey="price" 
                  stroke="#3b82f6" 
                  fill="url(#priceGradient)"
                  strokeWidth={2}
                  name="Price"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </>
      ) : null}
    </>
  );
  
  if (!showCard) return content;
  
  return (
    <Card data-testid="price-history-chart">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-blue-500" />
          Price History
          {title && <span className="text-sm font-normal text-gray-500">- {title}</span>}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {content}
      </CardContent>
    </Card>
  );
}

export function ComparisonPriceHistory({ listingIds, titles = [] }) {
  const { currency } = useCountry();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);
  
  useEffect(() => {
    if (listingIds?.length > 0) {
      fetchHistory();
    }
  }, [listingIds, period]);
  
  const fetchHistory = async () => {
    setLoading(true);
    try {
      const ids = listingIds.join(',');
      const response = await fetch(`${API_URL}/api/price-history/compare/${ids}?days=${period}`);
      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (err) {
      console.error('Error fetching comparison history:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Merge all history data into unified format for chart
  const getChartData = () => {
    if (!data?.listings) return [];
    
    // Get all unique dates
    const dateSet = new Set();
    data.listings.forEach(listing => {
      listing.history?.forEach(h => dateSet.add(h.date));
    });
    
    const dates = Array.from(dateSet).sort();
    
    // Build chart data
    return dates.map(date => {
      const point = { date };
      data.listings.forEach((listing, idx) => {
        const historyPoint = listing.history?.find(h => h.date === date);
        point[`price_${idx}`] = historyPoint?.price || null;
      });
      return point;
    });
  };
  
  return (
    <Card data-testid="comparison-price-history">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-blue-500" />
            Price History Comparison
          </CardTitle>
          
          {/* Period selector */}
          <div className="flex gap-1">
            {[7, 30, 90].map((days) => (
              <Button
                key={days}
                variant={period === days ? "default" : "outline"}
                size="sm"
                onClick={() => setPeriod(days)}
                className="h-7 px-2 text-xs"
              >
                {days}d
              </Button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="h-[250px] flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
          </div>
        ) : data?.listings?.length > 0 ? (
          <>
            {/* Legend */}
            <div className="flex flex-wrap gap-2 mb-4">
              {data.listings.map((listing, idx) => (
                <Badge 
                  key={idx} 
                  variant="outline" 
                  className="text-xs"
                  style={{ borderColor: CHART_COLORS[idx % CHART_COLORS.length] }}
                >
                  <span 
                    className="w-2 h-2 rounded-full mr-1"
                    style={{ backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] }}
                  />
                  {listing.title?.substring(0, 20)}...
                </Badge>
              ))}
            </div>
            
            {/* Chart */}
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={getChartData()} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 10 }}
                    tickFormatter={(value) => value ? new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                  />
                  <YAxis 
                    tick={{ fontSize: 10 }}
                    tickFormatter={(value) => `${currency}${(value/1000).toFixed(0)}k`}
                    width={50}
                  />
                  <Tooltip content={<CustomTooltip currency={currency} />} />
                  {data.listings.map((listing, idx) => (
                    <Line
                      key={idx}
                      type="monotone"
                      dataKey={`price_${idx}`}
                      stroke={CHART_COLORS[idx % CHART_COLORS.length]}
                      strokeWidth={2}
                      dot={false}
                      name={listing.title?.substring(0, 25) || `Property ${idx + 1}`}
                      connectNulls
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
            
            {/* Current prices */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
              {data.listings.map((listing, idx) => (
                <div 
                  key={idx} 
                  className="p-2 rounded-lg border text-center"
                  style={{ borderColor: CHART_COLORS[idx % CHART_COLORS.length] + '40' }}
                >
                  <p className="text-xs text-gray-500 truncate">{listing.title?.substring(0, 15)}...</p>
                  <p className="text-sm font-bold" style={{ color: CHART_COLORS[idx % CHART_COLORS.length] }}>
                    {currency}{listing.current_price?.toLocaleString()}/mo
                  </p>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="h-[250px] flex items-center justify-center text-gray-500">
            No price history available
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default SinglePriceHistory;
